/**
 * src/data/glossary.js — khung từ điển gọi vốn (80 mục gốc, bổ sung dần).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO CẦN 80 TRANG NÀY
 *
 * Mười ba trang công cụ nhắm vào truy vấn giao dịch — người đã biết mình cần
 * tính gì. Nhưng phần lớn founder lần đầu chưa tới mức đó: họ gõ "SAFE là gì",
 * "vesting nghĩa là sao", "pha loãng cổ phần tính thế nào".
 *
 * Đó là truy vấn đầu phễu, lượng tìm kiếm lớn hơn nhiều lần, cạnh tranh gần
 * như bằng không vì chưa ai viết tử tế bằng tiếng Việt. Mỗi trang từ điển là
 * một cửa vào, và cửa nào cũng dẫn tới một công cụ.
 *
 * Đây là lý do `relatedTools` là trường bắt buộc: một trang từ điển không dẫn
 * tới công cụ nào thì chỉ là bài viết, không phải sản phẩm.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * CÁCH DÙNG KHUNG NÀY
 *
 * `status: 'PLANNED'` là mới có khung, chưa viết nội dung. Viết xong thì thêm
 * `body` và `faq` rồi đổi sang `PUBLISHED`. Chỉ trang PUBLISHED mới lên
 * sitemap — tránh việc Google index hàng loạt trang rỗng, thứ làm hỏng đánh
 * giá chất lượng của cả tên miền.
 *
 * `priority`: 1 viết trước, 3 viết sau. Xếp theo hai tiêu chí — người mới hay
 * hỏi tới mức nào, và có dẫn được sang công cụ đã chạy hay không.
 */

import { daViet, NOI_DUNG as NOI_DUNG_KEYS } from './glossaryContent.js';

export const CUM = {
  CO_BAN: 'Khái niệm nền',
  CO_PHAN: 'Cổ phần và pha loãng',
  CONG_CU_VON: 'Công cụ đầu tư và điều khoản',
  DINH_GIA: 'Định giá',
  TAI_CHINH: 'Tài chính và chỉ số',
  PHAP_LY: 'Pháp lý Việt Nam',
  QUY_TRINH: 'Quy trình gọi vốn',
};

/** Rút gọn cách khai một mục. */
const t = (slug, term, cum, priority, relatedTools, shortDef, aliases = []) => ({
  slug, term, cum, priority, relatedTools, shortDef, aliases, status: 'PLANNED',
});

export const TU_DIEN = [
  /* ─── Khái niệm nền (12) ─────────────────────────────────────── */
  t('goi-von-la-gi', 'Gọi vốn', 'CO_BAN', 1, ['readiness-score'],
    'Bán một phần quyền sở hữu công ty để đổi lấy tiền phát triển kinh doanh.'),
  t('vong-pre-seed', 'Vòng pre-seed', 'CO_BAN', 1, ['readiness-score', 'raise-planner', 'round-simulator', 'fund-match'],
    'Vòng gọi vốn đầu tiên có tổ chức, thường khi đã có sản phẩm chạy được nhưng chưa có doanh thu đáng kể.'),
  t('vong-seed', 'Vòng seed', 'CO_BAN', 1, ['raise-planner', 'valuation-preseed', 'round-simulator', 'fund-match'],
    'Vòng sau pre-seed, thường khi đã có bằng chứng thị trường và bắt đầu có doanh thu.'),
  t('series-a', 'Series A', 'CO_BAN', 2, ['cap-table', 'round-simulator'],
    'Vòng vốn thể chế đầu tiên, thường yêu cầu doanh thu ổn định và mô hình đã kiểm chứng.'),
  t('nha-dau-tu-thien-than', 'Nhà đầu tư thiên thần', 'CO_BAN', 1, ['fund-match', 'valuation-preseed'],
    'Cá nhân dùng tiền của mình đầu tư vào startup giai đoạn rất sớm.', ['angel investor']),
  t('quy-dau-tu-mao-hiem', 'Quỹ đầu tư mạo hiểm', 'CO_BAN', 1, ['fund-match'],
    'Tổ chức huy động tiền từ nhà đầu tư khác rồi đầu tư vào startup, kỳ vọng hoàn vốn gấp nhiều lần.', ['VC']),
  t('vuon-uom-khoi-nghiep', 'Vườn ươm và chương trình tăng tốc', 'CO_BAN', 2, ['fund-match'],
    'Chương trình hỗ trợ startup giai đoạn sớm, thường kèm một khoản đầu tư nhỏ đổi lấy cổ phần.'),
  t('khau-vi-dau-tu', 'Khẩu vị đầu tư', 'CO_BAN', 1, ['fund-match'],
    'Bộ tiêu chí một quỹ dùng để quyết định xem xét hồ sơ nào: ngành, giai đoạn, địa bàn, quy mô séc.', ['investment thesis']),
  t('deal-flow', 'Deal flow', 'CO_BAN', 3, ['fund-match'],
    'Dòng hồ sơ đầu tư mà một quỹ tiếp nhận và sàng lọc.'),
  t('runway-la-gi', 'Runway', 'CO_BAN', 1, ['runway', 'raise-planner', 'readiness-score'],
    'Số tháng công ty còn hoạt động được với lượng tiền hiện có.'),
  t('bootstrapping', 'Bootstrapping', 'CO_BAN', 2, ['runway'],
    'Tự nuôi doanh nghiệp bằng doanh thu thay vì gọi vốn bên ngoài.'),
  t('exit-la-gi', 'Exit', 'CO_BAN', 3, ['cap-table'],
    'Thời điểm nhà đầu tư thu hồi vốn, thường qua bán công ty hoặc niêm yết.'),

  /* ─── Cổ phần và pha loãng (14) ──────────────────────────────── */
  t('pha-loang-co-phan', 'Pha loãng cổ phần', 'CO_PHAN', 1, ['round-simulator', 'cap-table', 'raise-planner', 'safe-convert', 'valuation-preseed', 'valuation-revenue', 'term-sheet', 'founder-equity-split'],
    'Tỷ lệ sở hữu của bạn giảm xuống khi công ty phát hành thêm cổ phần cho người mới.', ['dilution']),
  t('bang-co-phan', 'Bảng cổ phần', 'CO_PHAN', 1, ['cap-table', 'safe-convert', 'legal-checklist'],
    'Bảng ghi ai sở hữu bao nhiêu cổ phần và bao nhiêu phần trăm công ty.', ['cap table']),
  t('vesting-la-gi', 'Vesting', 'CO_PHAN', 1, ['founder-equity-split', 'readiness-score', 'legal-checklist'],
    'Cơ chế nhận cổ phần dần theo thời gian làm việc thay vì nhận hết ngay.'),
  t('cliff-vesting', 'Cliff', 'CO_PHAN', 1, ['founder-equity-split', 'legal-checklist'],
    'Khoảng thời gian đầu chưa nhận được cổ phần nào; đủ mốc mới nhận trọn phần đã tích luỹ.'),
  t('esop-la-gi', 'ESOP (quỹ cổ phiếu thưởng)', 'CO_PHAN', 1, ['cap-table', 'round-simulator', 'safe-convert', 'term-sheet'],
    'Phần cổ phần dành sẵn để thưởng cho nhân sự, thường 10–15% công ty.', ['option pool']),
  t('option-pool-shuffle', 'Option pool shuffle', 'CO_PHAN', 2, ['safe-convert', 'round-simulator', 'term-sheet'],
    'Điều khoản đưa quỹ cổ phiếu thưởng vào phần pre-money, khiến chỉ người sáng lập gánh phần pha loãng đó.'),
  t('co-phan-chet', 'Cổ phần chết', 'CO_PHAN', 2, ['founder-equity-split'],
    'Cổ phần nằm trong tay người không còn đóng góp cho công ty.', ['dead equity']),
  t('chia-co-phan-dong-sang-lap', 'Chia cổ phần đồng sáng lập', 'CO_PHAN', 1, ['founder-equity-split'],
    'Cách phân chia quyền sở hữu giữa những người cùng lập công ty.'),
  t('co-phan-pho-thong', 'Cổ phần phổ thông', 'CO_PHAN', 2, ['cap-table'],
    'Loại cổ phần người sáng lập và nhân sự nắm giữ, không có quyền ưu tiên đặc biệt.'),
  t('co-phan-uu-dai', 'Cổ phần ưu đãi', 'CO_PHAN', 2, ['term-sheet', 'cap-table'],
    'Loại cổ phần quỹ thường nhận, kèm quyền ưu tiên khi chia tài sản hoặc biểu quyết.'),
  t('pha-loang-toan-phan', 'Pha loãng toàn phần', 'CO_PHAN', 3, ['cap-table'],
    'Cách tính tỷ lệ sở hữu có tính cả cổ phần chưa phát hành như ESOP và SAFE chờ chuyển đổi.', ['fully diluted']),
  t('quyen-mua-them', 'Quyền mua thêm', 'CO_PHAN', 3, ['cap-table'],
    'Quyền của nhà đầu tư hiện hữu được mua thêm ở vòng sau để giữ nguyên tỷ lệ.', ['pro rata']),
  t('chong-pha-loang', 'Điều khoản chống pha loãng', 'CO_PHAN', 3, ['term-sheet', 'safe-convert'],
    'Cơ chế bảo vệ nhà đầu tư khi vòng sau định giá thấp hơn vòng họ tham gia.', ['anti-dilution']),
  t('vong-giam-gia', 'Vòng giảm giá', 'CO_PHAN', 3, ['valuation-preseed', 'term-sheet'],
    'Vòng gọi vốn có định giá thấp hơn vòng trước.', ['down round']),

  /* ─── Công cụ đầu tư và điều khoản (14) ──────────────────────── */
  t('safe-la-gi', 'SAFE', 'CONG_CU_VON', 1, ['safe-convert', 'round-simulator'],
    'Thoả thuận nhận tiền trước, quy đổi thành cổ phần ở vòng gọi vốn sau.'),
  t('safe-post-money', 'SAFE post-money', 'CONG_CU_VON', 1, ['safe-convert'],
    'Bản SAFE trong đó nhà đầu tư biết chắc tỷ lệ sở hữu; các SAFE không pha loãng lẫn nhau.'),
  t('safe-pre-money', 'SAFE pre-money', 'CONG_CU_VON', 2, ['safe-convert'],
    'Bản SAFE cũ hơn, trong đó các SAFE pha loãng lẫn nhau khi cùng chuyển đổi.'),
  t('valuation-cap', 'Valuation cap', 'CONG_CU_VON', 1, ['safe-convert', 'round-simulator'],
    'Mức định giá trần dùng để quy đổi SAFE, bảo vệ nhà đầu tư sớm khi vòng sau định giá cao.'),
  t('discount-safe', 'Discount', 'CONG_CU_VON', 1, ['safe-convert'],
    'Mức chiết khấu so với giá vòng sau mà nhà đầu tư SAFE được hưởng.'),
  t('trai-phieu-chuyen-doi', 'Trái phiếu chuyển đổi', 'CONG_CU_VON', 2, ['safe-convert'],
    'Khoản vay có thể chuyển thành cổ phần, khác SAFE ở chỗ có lãi suất và ngày đáo hạn.', ['convertible note']),
  t('term-sheet', 'Term sheet', 'CONG_CU_VON', 1, ['term-sheet', 'cap-table'],
    'Văn bản tóm tắt các điều khoản chính của thương vụ, thường chưa ràng buộc pháp lý.'),
  t('mfn-clause', 'Điều khoản MFN', 'CONG_CU_VON', 3, ['safe-convert'],
    'Cam kết cho nhà đầu tư hưởng điều khoản tốt nhất mà bạn ký với người khác sau đó.'),
  t('liquidation-preference', 'Quyền ưu tiên thanh toán', 'CONG_CU_VON', 2, ['cap-table', 'term-sheet'],
    'Quyền của nhà đầu tư được nhận tiền trước khi chia cho cổ đông phổ thông.'),
  t('ghe-hoi-dong-quan-tri', 'Ghế hội đồng quản trị', 'CONG_CU_VON', 2, ['cap-table'],
    'Quyền cử người vào hội đồng quản trị mà nhà đầu tư có thể yêu cầu.', ['board seat']),
  t('quyen-phu-quyet', 'Quyền phủ quyết', 'CONG_CU_VON', 3, ['cap-table'],
    'Quyền của nhà đầu tư chặn một số quyết định lớn của công ty.', ['veto right']),
  t('bridge-round', 'Vòng cầu nối', 'CONG_CU_VON', 2, ['runway', 'raise-planner'],
    'Khoản vốn nhỏ để duy trì hoạt động tới vòng gọi vốn chính thức tiếp theo.'),
  t('nha-dau-tu-dan-dat', 'Nhà đầu tư dẫn dắt', 'CONG_CU_VON', 2, ['fund-match'],
    'Quỹ đứng ra định giá, đàm phán điều khoản và thường bỏ phần lớn số tiền của vòng.', ['lead investor']),
  t('dong-vong-goi-von', 'Đóng vòng', 'CONG_CU_VON', 3, ['raise-planner'],
    'Thời điểm ký xong giấy tờ và tiền thực sự về tài khoản công ty.'),

  /* ─── Định giá (10) ──────────────────────────────────────────── */
  t('dinh-gia-startup', 'Định giá startup', 'DINH_GIA', 1, ['valuation-preseed', 'valuation-revenue'],
    'Ước lượng giá trị công ty làm cơ sở tính tỷ lệ cổ phần bán ra.'),
  t('pre-money-post-money', 'Pre-money và post-money', 'DINH_GIA', 1, ['valuation-preseed', 'round-simulator', 'raise-planner', 'safe-convert', 'term-sheet', 'valuation-revenue'],
    'Giá trị công ty ngay trước và ngay sau khi tiền đầu tư vào; chênh nhau đúng bằng số tiền gọi được.',
    ['định giá trước và sau khi nhận vốn', 'định giá trước tiền', 'định giá sau tiền', 'định giá trước vốn', 'định giá sau vốn']),
  t('phuong-phap-scorecard', 'Phương pháp chấm điểm', 'DINH_GIA', 2, ['valuation-preseed'],
    'Cách định giá lấy mặt bằng thị trường làm gốc rồi điều chỉnh theo từng trục của hồ sơ.'),
  t('boi-so-doanh-thu', 'Bội số doanh thu', 'DINH_GIA', 2, ['valuation-revenue', 'valuation-preseed'],
    'Cách định giá lấy doanh thu nhân với một hệ số; không dùng được ở giai đoạn chưa có doanh thu.'),
  t('dinh-gia-qua-cao', 'Định giá quá cao', 'DINH_GIA', 2, ['valuation-preseed', 'valuation-revenue'],
    'Định giá vượt xa mức tương xứng với cột mốc đã đạt, khiến vòng sau khó gọi.'),
  t('so-tien-goi-von', 'Gọi bao nhiêu là hợp lý', 'DINH_GIA', 1, ['raise-planner', 'pitch-deck', 'fund-match'],
    'Cách tính số tiền cần gọi từ mức đốt tiền và cột mốc muốn đạt.'),
  t('su-dung-von', 'Kế hoạch sử dụng vốn', 'DINH_GIA', 2, ['raise-planner', 'pitch-deck'],
    'Bảng phân bổ số tiền gọi được theo từng hạng mục và kết quả tương ứng.', ['use of funds']),
  t('cot-moc-goi-von', 'Cột mốc giữa hai vòng', 'DINH_GIA', 2, ['raise-planner'],
    'Kết quả cụ thể cần đạt để vòng sau có cơ sở định giá cao hơn.', ['milestone']),
  t('quy-mo-thi-truong', 'Quy mô thị trường TAM SAM SOM', 'DINH_GIA', 1, ['market-sizing', 'pitch-deck', 'readiness-score'],
    'Ba mức thị trường: toàn bộ, phần tiếp cận được, và phần thực sự giành được.'),
  t('bottom-up-market-sizing', 'Ước lượng thị trường từ dưới lên', 'DINH_GIA', 2, ['market-sizing'],
    'Dựng quy mô thị trường từ số khách hàng và giá trị mỗi khách, thay vì lấy số báo cáo ngành.'),

  /* ─── Tài chính và chỉ số (14) ───────────────────────────────── */
  t('burn-rate', 'Mức đốt tiền', 'TAI_CHINH', 1, ['runway', 'raise-planner'],
    'Số tiền công ty chi ra mỗi tháng; đốt ròng là phần còn lại sau khi trừ doanh thu.'),
  t('gross-burn-net-burn', 'Đốt gộp và đốt ròng', 'TAI_CHINH', 2, ['runway'],
    'Đốt gộp là tổng chi; đốt ròng là tổng chi trừ doanh thu. Đây mới là số tiền thực sự mất đi.'),
  t('cac-la-gi', 'CAC (chi phí có được khách hàng)', 'TAI_CHINH', 1, ['unit-economics-check'],
    'Tổng chi bán hàng và marketing chia cho số khách hàng mới có được.'),
  t('ltv-la-gi', 'LTV (giá trị vòng đời khách hàng)', 'TAI_CHINH', 1, ['unit-economics-check'],
    'Tổng lãi gộp một khách hàng mang lại trong suốt thời gian họ còn dùng sản phẩm.'),
  t('ty-le-ltv-cac', 'Tỷ lệ LTV trên CAC', 'TAI_CHINH', 1, ['unit-economics-check'],
    'Mỗi đồng bỏ ra để có khách mang về bao nhiêu đồng lãi gộp; chuẩn thị trường từ 3 lần.'),
  t('payback-period', 'Thời gian hoàn vốn khách hàng', 'TAI_CHINH', 2, ['unit-economics-check'],
    'Số tháng để lãi gộp từ một khách bù lại chi phí có được họ.'),
  t('churn-rate', 'Tỷ lệ rời bỏ', 'TAI_CHINH', 1, ['growth-metrics', 'unit-economics-check'],
    'Phần trăm khách hàng ngừng sử dụng sản phẩm trong một kỳ.'),
  t('mrr-arr', 'MRR và ARR', 'TAI_CHINH', 1, ['growth-metrics', 'unit-economics-check'],
    'Doanh thu định kỳ tính theo tháng và theo năm.'),
  t('bien-loi-nhuan-gop', 'Biên lợi nhuận gộp', 'TAI_CHINH', 2, ['unit-economics-check'],
    'Phần trăm doanh thu còn lại sau khi trừ chi phí trực tiếp phục vụ khách hàng.'),
  t('kinh-te-don-vi', 'Kinh tế đơn vị', 'TAI_CHINH', 1, ['unit-economics-check'],
    'Bài toán lời lỗ trên một khách hàng, tách khỏi chi phí cố định của cả doanh nghiệp.', ['unit economics']),
  t('mo-hinh-tai-chinh', 'Mô hình tài chính', 'TAI_CHINH', 1, ['raise-planner', 'market-sizing', 'valuation-revenue'],
    'Bảng dự báo doanh thu, chi phí và dòng tiền theo từng tháng.'),
  t('diem-hoa-von', 'Điểm hoà vốn', 'TAI_CHINH', 2, ['raise-planner'],
    'Thời điểm lãi gộp đủ bù chi phí, công ty không còn đốt tiền.'),
  t('burn-multiple', 'Burn multiple', 'TAI_CHINH', 3, ['runway'],
    'Số tiền đốt để tạo ra mỗi đồng doanh thu định kỳ mới; đo hiệu quả sử dụng vốn.'),
  t('cohort-la-gi', 'Cohort', 'TAI_CHINH', 2, ['unit-economics-check'],
    'Nhóm khách hàng bắt đầu cùng một kỳ, theo dõi riêng để đo tỷ lệ giữ chân.'),

  /* ─── Pháp lý Việt Nam (8) ───────────────────────────────────── */
  t('loai-hinh-doanh-nghiep-startup', 'Chọn loại hình doanh nghiệp', 'PHAP_LY', 1, ['legal-checklist'],
    'So sánh công ty cổ phần, TNHH và hộ kinh doanh cho mục đích gọi vốn.'),
  t('cong-ty-co-phan-goi-von', 'Vì sao startup thường chọn công ty cổ phần', 'PHAP_LY', 1, ['legal-checklist'],
    'Loại hình linh hoạt nhất cho việc phát hành cổ phần và lập quỹ cổ phiếu thưởng.'),
  t('nghi-dinh-38-2018', 'Nghị định 38/2018 về quỹ đầu tư khởi nghiệp', 'PHAP_LY', 2, ['fund-match'],
    'Khuôn khổ pháp lý cho quỹ đầu tư khởi nghiệp sáng tạo tại Việt Nam.'),
  t('chuyen-giao-so-huu-tri-tue', 'Chuyển giao sở hữu trí tuệ về công ty', 'PHAP_LY', 1, ['legal-checklist', 'readiness-score'],
    'Thủ tục đưa mã nguồn, thiết kế và thương hiệu từ tên cá nhân về tên công ty.'),
  t('dieu-le-cong-ty', 'Điều lệ công ty', 'PHAP_LY', 2, ['legal-checklist'],
    'Văn bản quy định cách công ty vận hành; bản mẫu thường thiếu điều khoản nhà đầu tư cần.'),
  t('thoa-thuan-dong-sang-lap', 'Thoả thuận đồng sáng lập', 'PHAP_LY', 1, ['legal-checklist', 'founder-equity-split'],
    'Văn bản ghi rõ phân chia cổ phần, vai trò, cách ra quyết định và điều gì xảy ra khi có người rời đi.'),
  t('dang-ky-nhan-hieu', 'Đăng ký nhãn hiệu', 'PHAP_LY', 2, ['legal-checklist'],
    'Ở Việt Nam nhãn hiệu theo nguyên tắc ai nộp trước được trước.'),
  t('nghi-dinh-13-2023', 'Nghị định 13/2023 về dữ liệu cá nhân', 'PHAP_LY', 2, ['legal-checklist'],
    'Nghĩa vụ của doanh nghiệp thu thập dữ liệu người dùng tại Việt Nam.'),

  /* ─── Quy trình gọi vốn (8) ──────────────────────────────────── */
  t('pitch-deck-la-gi', 'Pitch deck', 'QUY_TRINH', 1, ['pitch-deck'],
    'Bộ slide giới thiệu doanh nghiệp gửi cho nhà đầu tư, thường 10–15 trang.', ['slide deck', 'deck gọi vốn']),
  t('data-room', 'Data room', 'QUY_TRINH', 2, ['legal-checklist', 'pitch-deck'],
    'Kho tài liệu công ty chuẩn bị sẵn cho nhà đầu tư xem trong quá trình thẩm định.'),
  t('due-diligence', 'Thẩm định', 'QUY_TRINH', 1, ['legal-checklist', 'readiness-score'],
    'Quá trình nhà đầu tư kiểm tra pháp lý, tài chính và vận hành trước khi xuống tiền.', ['due diligence']),
  t('warm-intro', 'Giới thiệu qua người quen', 'QUY_TRINH', 2, ['fund-match'],
    'Cách tiếp cận nhà đầu tư qua người họ đã tin, hiệu quả hơn nhiều so với gửi thư lạnh.'),
  t('investor-update', 'Thư cập nhật nhà đầu tư', 'QUY_TRINH', 3, ['runway'],
    'Bản tin định kỳ gửi nhà đầu tư về tình hình công ty.'),
  t('thu-cam-ket-mua', 'Thư cam kết mua (LOI)', 'QUY_TRINH', 2, ['readiness-score'],
    'Văn bản khách hàng tiềm năng xác nhận ý định mua, dùng làm bằng chứng nhu cầu.'),
  t('trien-khai-thu', 'Triển khai thử (pilot)', 'QUY_TRINH', 2, ['readiness-score'],
    'Cho khách hàng dùng thử có kiểm soát; bản có thu phí đáng giá hơn nhiều bản miễn phí.'),
  t('bi-tu-choi-goi-von', 'Bị từ chối và cách xử lý', 'QUY_TRINH', 2, ['readiness-score'],
    'Cách đọc lý do từ chối và điều chỉnh hồ sơ trước khi gặp quỹ tiếp theo.'),
  /* ─── Bổ sung 10/2026 (nội dung ở glossaryMoi.js) ─────────────── */
  t('mvp-la-gi', 'MVP (sản phẩm khả dụng tối thiểu)', 'CO_BAN', 1, ['readiness-score'],
    'Phiên bản đơn giản nhất của sản phẩm vẫn giải quyết được vấn đề chính cho khách hàng thật.', ['minimum viable product']),
  t('product-market-fit', 'Product-market fit', 'CO_BAN', 1, ['growth-metrics', 'readiness-score'],
    'Lúc sản phẩm đáp ứng đúng nhu cầu của một thị trường đủ lớn: khách tự tìm đến, ở lại và giới thiệu người khác.', ['PMF']),
  t('traction-la-gi', 'Traction', 'CO_BAN', 1, ['growth-metrics', 'readiness-score', 'market-sizing', 'pitch-deck'],
    'Bằng chứng bằng số cho thấy thị trường đang đón nhận sản phẩm: doanh thu, khách trả tiền, tốc độ tăng.'),
  t('tai-tro-khong-hoan-lai', 'Tài trợ không hoàn lại', 'CO_BAN', 2, ['raise-planner', 'fund-match'],
    'Khoản tiền cấp cho doanh nghiệp không lấy cổ phần và không đòi trả lại, đổi lại phải dùng đúng mục đích.', ['grant']),
  t('cvc-quy-doanh-nghiep', 'Quỹ đầu tư doanh nghiệp', 'CO_BAN', 2, ['fund-match'],
    'Đơn vị đầu tư mạo hiểm thuộc một tập đoàn, đầu tư vì cả lợi nhuận lẫn lợi ích chiến lược.', ['CVC', 'corporate venture capital']),
  t('elevator-pitch', 'Elevator pitch', 'QUY_TRINH', 2, ['pitch-deck'],
    'Phần giới thiệu doanh nghiệp trong 30 tới 60 giây, đủ để người nghe muốn hỏi thêm.'),
  t('one-pager-teaser', 'One-pager', 'QUY_TRINH', 2, ['pitch-deck'],
    'Bản tóm tắt doanh nghiệp trong một trang, dùng để mở lời trước khi gửi pitch deck.', ['teaser']),
  t('email-goi-nha-dau-tu', 'Email gửi nhà đầu tư lần đầu', 'QUY_TRINH', 2, ['pitch-deck', 'fund-match'],
    'Thư giới thiệu ngắn với một mục tiêu duy nhất: có được cuộc gặp.', ['cold email']),
  t('nda-goi-von', 'NDA khi gọi vốn', 'QUY_TRINH', 3, ['legal-checklist'],
    'Thoả thuận bảo mật; phần lớn quỹ không ký ở bước đầu, hợp lý hơn khi tới giai đoạn thẩm định.', ['thoả thuận bảo mật']),
  t('thoa-thuan-co-dong', 'Thoả thuận cổ đông', 'CONG_CU_VON', 1, ['term-sheet', 'legal-checklist'],
    'Hợp đồng giữa các cổ đông về quyền, nghĩa vụ, cách ra quyết định và chuyển nhượng cổ phần.', ['SHA', 'shareholders agreement']),
  t('hop-dong-mua-co-phan-spa', 'Hợp đồng mua cổ phần', 'CONG_CU_VON', 2, ['term-sheet'],
    'Hợp đồng ghi nhận nhà đầu tư mua bao nhiêu cổ phần, giá bao nhiêu và điều kiện để giải ngân.', ['SPA']),
  t('drag-along-tag-along', 'Drag-along và tag-along', 'CONG_CU_VON', 2, ['term-sheet'],
    'Hai điều khoản về bán công ty: quyền buộc cổ đông nhỏ bán theo, và quyền được bán theo cổ đông lớn.', ['quyền kéo theo']),
  t('quyen-uu-tien-mua-rofr', 'Quyền ưu tiên mua', 'CONG_CU_VON', 2, ['term-sheet', 'cap-table'],
    'Quyền được mua trước, cùng giá, khi một cổ đông định bán cổ phần cho người ngoài.', ['ROFR', 'right of first refusal']),
  t('vesting-tang-toc', 'Tăng tốc vesting', 'CO_PHAN', 2, ['founder-equity-split', 'term-sheet'],
    'Điều khoản cho phần cổ phần chưa vesting được vesting sớm khi công ty được bán.', ['acceleration', 'double trigger']),
  t('good-leaver-bad-leaver', 'Good leaver và bad leaver', 'CO_PHAN', 2, ['founder-equity-split'],
    'Cách phân loại người rời công ty để quyết định họ giữ bao nhiêu cổ phần và bán lại với giá nào.'),
  t('phuong-phap-vc', 'Phương pháp VC', 'DINH_GIA', 2, ['valuation-preseed', 'valuation-revenue'],
    'Định giá đi ngược từ giá trị kỳ vọng khi exit, chia cho mức lợi nhuận nhà đầu tư cần.', ['VC method']),
  t('phuong-phap-berkus', 'Phương pháp Berkus', 'DINH_GIA', 3, ['valuation-preseed'],
    'Định giá startup chưa có doanh thu bằng cách cộng giá trị cho năm yếu tố giảm rủi ro.'),
  t('dinh-gia-dcf', 'Định giá chiết khấu dòng tiền', 'DINH_GIA', 2, ['valuation-revenue'],
    'Quy dòng tiền dự kiến trong tương lai về giá trị hôm nay theo một tỷ lệ chiết khấu phản ánh rủi ro.', ['DCF']),
  t('tang-truong-mom', 'Tăng trưởng theo tháng', 'TAI_CHINH', 1, ['growth-metrics'],
    'Phần trăm thay đổi của một chỉ số so với tháng trước; CMGR là tốc độ bình quân kép qua nhiều tháng.', ['MoM', 'CMGR']),
  t('nrr-net-revenue-retention', 'Giữ chân doanh thu ròng', 'TAI_CHINH', 2, ['growth-metrics', 'unit-economics-check'],
    'Doanh thu từ khách hàng hiện có sau một kỳ, tính cả phần mua thêm, giảm gói và rời đi.', ['NRR', 'net revenue retention']),
  t('gmv-take-rate', 'GMV và take rate', 'TAI_CHINH', 2, ['unit-economics-check', 'growth-metrics'],
    'Tổng giá trị giao dịch qua nền tảng, và phần trăm nền tảng giữ lại làm doanh thu.', ['GMV']),
  t('ebitda-la-gi', 'EBITDA', 'TAI_CHINH', 3, ['raise-planner', 'valuation-revenue'],
    'Lợi nhuận trước lãi vay, thuế và khấu hao: hoạt động kinh doanh cốt lõi tạo ra bao nhiêu.'),
  t('von-dieu-le', 'Vốn điều lệ', 'PHAP_LY', 1, ['legal-checklist'],
    'Tổng vốn thành viên, cổ đông đã góp hoặc cam kết góp, ghi trong điều lệ và đăng ký doanh nghiệp.', ['charter capital']),
  t('cau-truc-holding-singapore', 'Cấu trúc holding ở Singapore', 'PHAP_LY', 1, ['legal-checklist', 'fund-match'],
    'Lập công ty mẹ ở Singapore nắm công ty Việt Nam, để nhà đầu tư nước ngoài rót vốn vào công ty mẹ.', ['flip', 'holding Singapore']),
  /* ─── Bổ sung 19/10/2026 (từ bộ tài liệu định giá; nội dung ở glossaryMoi.js) ─── */
  t('phan-ky-dinh-gia', 'Phân kỳ định giá', 'DINH_GIA', 1, ['round-simulator', 'cap-table'],
    'Khoảng chênh giữa mức tăng giá trị công ty và mức tăng giá trị cổ phần của nhà đầu tư, do bị pha loãng qua các vòng sau.', ['valuation divergence']),
  t('chiet-khau-kho-chuyen-nhuong', 'Chiết khấu do khó chuyển nhượng', 'DINH_GIA', 3, ['valuation-revenue'],
    'Mức giảm giá áp lên cổ phần công ty chưa niêm yết, vì người nắm giữ không bán lại được dễ dàng.', ['DLOM', 'discount for lack of marketability']),
  t('chung-quyen-warrant', 'Chứng quyền', 'CONG_CU_VON', 2, ['term-sheet', 'cap-table'],
    'Quyền mua thêm cổ phần trong tương lai với giá định trước, thường được tặng kèm một khoản đầu tư hoặc khoản vay.', ['warrant']),
  t('co-tuc-tich-luy', 'Cổ tức tích luỹ', 'CONG_CU_VON', 3, ['term-sheet'],
    'Cổ tức cộng dồn mỗi năm cho cổ phần ưu đãi, không trả bằng tiền mà cộng vào số tiền nhà đầu tư nhận khi bán công ty.', ['cumulative dividend', 'accruing dividend']),
  t('thac-phan-phoi-thanh-ly', 'Thác phân phối khi bán công ty', 'CONG_CU_VON', 2, ['term-sheet', 'round-simulator'],
    'Thứ tự chia tiền khi công ty được bán hoặc giải thể: chủ nợ trước, cổ phần ưu đãi sau, cổ phần phổ thông cuối cùng.', ['liquidation waterfall', 'waterfall']),
  t('pari-passu', 'Pari passu', 'CONG_CU_VON', 3, ['term-sheet'],
    'Ngang hàng: các bên được chia cùng một lượt và theo cùng tỷ lệ, không bên nào được ưu tiên hơn bên nào.', ['ngang hàng']),
  t('quyen-thong-tin', 'Quyền thông tin', 'CONG_CU_VON', 2, ['term-sheet'],
    'Quyền của nhà đầu tư được nhận báo cáo tài chính và số liệu hoạt động định kỳ từ công ty.', ['information rights']),
  t('quyen-yeu-cau-mua-lai', 'Quyền yêu cầu mua lại cổ phần', 'CONG_CU_VON', 3, ['term-sheet'],
    'Quyền của nhà đầu tư buộc công ty mua lại cổ phần của mình sau một thời hạn, thường 5 năm, nếu chưa có exit.', ['redemption right', 'redemption']),
  t('dieu-khoan-no-shop', 'Điều khoản no-shop', 'CONG_CU_VON', 2, ['term-sheet'],
    'Cam kết trong term sheet rằng startup không chào bán cho nhà đầu tư khác trong một thời gian, thường 30 tới 60 ngày.', ['no-shop', 'exclusivity']),
  t('cam-doan-bao-dam', 'Cam đoan và bảo đảm', 'CONG_CU_VON', 2, ['legal-checklist', 'term-sheet'],
    'Những điều công ty và founder khẳng định là đúng trong hợp đồng đầu tư; nếu sai, bên cam đoan phải chịu trách nhiệm.', ['representations and warranties', 'R&W']),
  t('vong-tang-gia', 'Vòng tăng giá', 'CO_PHAN', 2, ['round-simulator', 'cap-table'],
    'Vòng gọi vốn có giá mỗi cổ phần cao hơn vòng trước.', ['up round']),
  t('co-phan-doi-cong-suc', 'Cổ phần đổi bằng công sức', 'CO_PHAN', 2, ['founder-equity-split', 'cap-table'],
    'Cổ phần trao cho người góp sức lao động thay vì góp tiền, phổ biến với founder và nhân sự đầu tiên.', ['sweat equity']),
  t('dong-dau-tu', 'Đồng đầu tư', 'QUY_TRINH', 2, ['fund-match'],
    'Nhiều nhà đầu tư cùng góp vào một vòng, thường có một bên dẫn dắt đặt điều khoản và các bên khác đi theo.', ['syndication', 'co-investment', 'syndicate']),
  t('magic-number-saas', 'Magic number', 'TAI_CHINH', 2, ['growth-metrics', 'unit-economics-check'],
    'Doanh thu định kỳ năm tăng thêm trên mỗi đồng chi bán hàng và marketing; trên 1 là hiệu quả.', ['sales efficiency']),
  t('arpu-acv', 'ARPU và ACV', 'TAI_CHINH', 2, ['unit-economics-check', 'growth-metrics', 'market-sizing'],
    'Doanh thu bình quân trên mỗi khách hàng theo tháng, và giá trị hợp đồng bình quân tính theo năm.', ['ARPU', 'ACV']),
  t('gia-tri-doanh-nghiep', 'Giá trị doanh nghiệp', 'DINH_GIA', 2, ['valuation-revenue'],
    'Giá trị của toàn bộ hoạt động kinh doanh, chưa tính tiền mặt công ty đang giữ và nợ vay; cộng tiền mặt, trừ nợ mới ra giá trị phần vốn cổ đông.', ['enterprise value', 'EV']),
  t('boi-so-ebitda', 'Bội số EBITDA', 'DINH_GIA', 2, ['valuation-revenue'],
    'Giá trị doanh nghiệp chia cho EBITDA 12 tháng; cách định giá phổ biến cho công ty đã có lãi ổn định.', ['EV/EBITDA', 'EBITDA multiple']),
  t('nhom-so-sanh', 'Nhóm so sánh', 'DINH_GIA', 2, ['valuation-revenue', 'valuation-preseed'],
    'Các doanh nghiệp niêm yết hoặc thương vụ cùng ngành, cùng quy mô dùng làm thước đo để suy ra bội số định giá.', ['comparables', 'comps']),
  t('uoc-luong-tu-tren-xuong', 'Ước lượng thị trường từ trên xuống', 'TAI_CHINH', 3, ['market-sizing'],
    'Lấy quy mô toàn ngành từ báo cáo rồi nhân với một tỷ lệ giả định; nhanh nhưng khó thuyết phục nhà đầu tư.', ['top-down market sizing', 'top-down']),
  t('uu-tien-tham-gia', 'Ưu tiên có tham gia chia tiếp', 'CONG_CU_VON', 2, ['term-sheet'],
    'Nhà đầu tư lấy lại phần ưu tiên khi bán công ty rồi vẫn được chia tiếp phần còn lại theo tỷ lệ sở hữu.', ['participating preferred', 'double dip']),
  t('boi-so-nhan-von', 'Bội số nhân vốn', 'DINH_GIA', 2, ['valuation-revenue', 'valuation-preseed', 'term-sheet'],
    'Số lần tiền nhà đầu tư nhận về so với tiền bỏ vào; quỹ giai đoạn sớm nhắm mỗi khoản có khả năng nhân 10 lần trở lên.', ['MOIC', 'cash-on-cash multiple']),
  t('gia-moi-co-phan', 'Giá mỗi cổ phần', 'CO_PHAN', 2, ['safe-convert', 'cap-table', 'round-simulator'],
    'Định giá pre-money chia cho tổng số cổ phần trước vòng; nhà đầu tư mới mua cổ phần ở giá này.', ['price per share', 'PPS']),
  t('doanh-thu-chay-nam', 'Doanh thu chạy năm', 'TAI_CHINH', 2, ['growth-metrics', 'valuation-revenue'],
    'Doanh thu tháng gần nhất nhân 12; cho biết quy mô hiện tại nếu giữ nguyên nhịp này, khác với doanh thu 12 tháng đã thực thu.', ['run-rate', 'annualized revenue']),
  t('quy-mo-khoan-dau-tu', 'Quy mô khoản đầu tư', 'QUY_TRINH', 2, ['fund-match'],
    'Khoảng số tiền một quỹ thường bỏ vào mỗi startup; gọi ngoài khoảng này thì hồ sơ thường bị loại từ vòng đầu.', ['ticket size', 'check size']),  /* ─── Bổ sung 11/2026: toán học của quỹ và 5T (nội dung ở glossaryMoi.js) ── */
  t('lp-va-gp', 'LP và GP', 'CO_BAN', 2, ['fund-match'],
    'LP là người góp tiền vào quỹ đầu tư, GP là đội ngũ quản lý quỹ, chọn startup và chịu trách nhiệm trả lợi nhuận cho LP.', ['limited partner', 'general partner']),
  t('phi-quan-ly-quy', 'Phí quản lý quỹ', 'CO_BAN', 2, ['fund-match'],
    'Khoản quỹ trích mỗi năm, thường khoảng 2% số vốn cam kết, để trả lương, văn phòng và chi phí vận hành; phần này không được đem đi đầu tư.', ['management fee', '2 và 20']),
  t('carry', 'Carry', 'CO_BAN', 2, ['fund-match'],
    'Phần lợi nhuận đội ngũ quản lý quỹ được chia, thường khoảng 20%, chỉ khi quỹ trả cho người góp vốn vượt mức lãi đã thoả thuận.', ['carried interest', 'phần chia lợi nhuận của quỹ']),
  t('luat-luy-thua', 'Luật lũy thừa trong đầu tư mạo hiểm', 'CO_BAN', 1, ['fund-match', 'readiness-score'],
    'Lợi nhuận của một quỹ dồn vào rất ít công ty: phần lớn khoản đầu tư lỗ hoặc hoà, một hai công ty thắng lớn gánh cả quỹ.', ['power law']),
  t('cong-ty-tra-lai-ca-quy', 'Công ty trả lại cả quỹ', 'CO_BAN', 1, ['market-sizing', 'fund-match'],
    'Một khoản đầu tư mà riêng nó mang về số tiền bằng hoặc hơn toàn bộ quy mô quỹ; quỹ chỉ rót vốn khi thấy startup có khả năng thành công ty như vậy.', ['fund returner']),
  t('don-bay-mo-rong', 'Đòn bẩy mở rộng', 'CO_BAN', 1, ['readiness-score', 'pitch-deck', 'unit-economics-check'],
    'Khả năng tăng số khách và doanh thu nhanh hơn nhiều so với tăng chi phí, nhờ công nghệ hoặc quy trình; chữ Technology trong 5T nhà đầu tư dùng.', ['scalability', 'Technology trong 5T']),
  t('hao-phong-thu', 'Hào phòng thủ', 'CO_BAN', 1, ['readiness-score', 'pitch-deck'],
    'Lợi thế khiến đối thủ khó sao chép và khách khó rời đi, như dữ liệu riêng, hiệu ứng mạng hay chi phí chuyển đổi; chữ Trenches trong 5T.', ['moat', 'Trenches trong 5T', 'lợi thế cạnh tranh bền vững']),
];

/**
 * Trạng thái suy ra từ việc đã có nội dung hay chưa, thay vì khai tay ở từng
 * mục. Viết xong một trang trong glossaryContent.js là nó tự lên sitemap —
 * không có bước "nhớ đổi status" để quên.
 */
for (const x of TU_DIEN) {
  x.status = daViet(x.slug) ? 'PUBLISHED' : 'PLANNED';
}

/* ------------------------------------------------------------------ */
/* Mẫu nội dung một trang                                              */
/* ------------------------------------------------------------------ */

/**
 * Cấu trúc mỗi trang, theo đúng thứ tự. Giữ nguyên cấu trúc cho cả 80 trang —
 * người đọc quen dần và tìm được thứ mình cần nhanh hơn, còn Google nhận ra
 * mẫu lặp lại là dấu hiệu nội dung có hệ thống.
 *
 *   1. Một câu định nghĩa      — trả lời ngay, không vòng vo
 *   2. Giải thích 2–3 đoạn     — bằng ngôn ngữ đời thường, không dùng thuật ngữ chưa giải thích
 *   3. Ví dụ bằng số VNĐ       — bắt buộc, và phải là số Việt Nam
 *   4. Sai lầm thường gặp      — phần người đọc nhớ lâu nhất
 *   5. Liên kết sang công cụ   — bắt buộc, ít nhất một
 *   6. Hai tới ba câu hỏi      — xuất ra JSON-LD dạng FAQPage
 *
 * Độ dài mục tiêu 500–800 từ. Ngắn hơn thì Google coi là mỏng; dài hơn thì
 * người đọc bỏ giữa chừng, mà mục đích của trang là dẫn sang công cụ.
 */
export const MAU_TRANG = {
  slug: 'vi-du',
  term: 'Thuật ngữ',
  cum: 'CO_BAN',
  priority: 1,
  relatedTools: ['runway'],
  shortDef: 'Một câu định nghĩa.',
  status: 'PUBLISHED',
  body: [
    { h: 'Nói ngắn gọn', p: ['…'] },
    { h: 'Ví dụ bằng số cụ thể', p: ['…'] },
    { h: 'Sai lầm thường gặp', p: ['…'] },
  ],
  faq: [{ q: '…', a: '…' }],
};

/* ------------------------------------------------------------------ */
/* Tra cứu                                                             */
/* ------------------------------------------------------------------ */

export function tuDienTheoSlug(slug) {
  return TU_DIEN.find((x) => x.slug === slug) || null;
}

/**
 * Tên tiếng Anh / viết tắt chuẩn cho các mục đặt tên tiếng Việt. Gộp vào
 * `aliases` để tìm được bằng tiếng Anh, và hiện cạnh tên trên trang Từ điển.
 * Mục đặt tên bằng tiếng Anh (Runway, SAFE, Vesting…) không cần.
 */
const TEN_ANH = {
  'goi-von-la-gi': 'fundraising',
  'vong-pre-seed': 'pre-seed round',
  'vong-seed': 'seed round',
  'vuon-uom-khoi-nghiep': 'incubator, accelerator',
  'chia-co-phan-dong-sang-lap': 'founder equity split',
  'co-phan-pho-thong': 'common shares',
  'co-phan-uu-dai': 'preferred shares',
  'liquidation-preference': 'liquidation preference',
  'bridge-round': 'bridge round',
  'dong-vong-goi-von': 'closing',
  'mfn-clause': 'most favored nation',
  'dinh-gia-startup': 'startup valuation',
  'phuong-phap-scorecard': 'scorecard method',
  'boi-so-doanh-thu': 'revenue multiple',
  'dinh-gia-qua-cao': 'overvaluation',
  'so-tien-goi-von': 'raise size',
  'quy-mo-thi-truong': 'market sizing',
  'bottom-up-market-sizing': 'bottom-up market sizing',
  'burn-rate': 'burn rate',
  'gross-burn-net-burn': 'gross burn, net burn',
  'cac-la-gi': 'customer acquisition cost',
  'ltv-la-gi': 'lifetime value',
  'ty-le-ltv-cac': 'LTV/CAC',
  'payback-period': 'CAC payback',
  'churn-rate': 'churn rate',
  'bien-loi-nhuan-gop': 'gross margin',
  'mo-hinh-tai-chinh': 'financial model',
  'diem-hoa-von': 'break-even point',
  'loai-hinh-doanh-nghiep-startup': 'legal entity',
  'cong-ty-co-phan-goi-von': 'joint stock company, JSC',
  'chuyen-giao-so-huu-tri-tue': 'IP assignment',
  'dieu-le-cong-ty': 'company charter',
  'thoa-thuan-dong-sang-lap': "founders' agreement",
  'dang-ky-nhan-hieu': 'trademark registration',
  'warm-intro': 'warm intro',
  'investor-update': 'investor update',
  'bi-tu-choi-goi-von': 'investor rejection',
};
for (const x of TU_DIEN) {
  const en = TEN_ANH[x.slug];
  if (en && !x.aliases.includes(en)) x.aliases = [en, ...x.aliases];
}

/** Tên tiếng Anh/viết tắt hiển thị cạnh tên mục (alias đầu tiên, nếu có). */
export const tenPhu = (x) => x.aliases?.[0] || '';

/** Khái niệm nền cho người mới, theo thứ tự nên đọc. */
export const NHAP_MON = [
  'goi-von-la-gi', 'vong-pre-seed', 'runway-la-gi', 'pre-money-post-money', 'pha-loang-co-phan',
  'bang-co-phan', 'vesting-la-gi', 'safe-la-gi', 'pitch-deck-la-gi', 'due-diligence',
];

/** Chỉ trang đã viết xong mới lên sitemap. */
export const DA_XUAT_BAN = TU_DIEN.filter((x) => x.status === 'PUBLISHED');

/**
 * Thuật ngữ liên quan hiện cuối mỗi trang. Ưu tiên danh sách viết tay trong
 * nội dung (`lienQuan`), thiếu thì bù bằng các mục cùng cụm, ưu tiên cao trước.
 * Đây là lớp liên kết nội bộ chính giữa các trang từ điển.
 */
export function thuatNguLienQuan(slug, vietTay = [], soLuong = 5) {
  const chon = [];
  const them = (x) => {
    if (x && x.slug !== slug && x.status === 'PUBLISHED' && !chon.includes(x)) chon.push(x);
  };
  for (const s of vietTay || []) them(tuDienTheoSlug(s));
  const goc = tuDienTheoSlug(slug);
  if (goc) {
    TU_DIEN.filter((x) => x.cum === goc.cum)
      .sort((a, b) => a.priority - b.priority)
      .forEach((x) => chon.length < soLuong && them(x));
  }
  return chon.slice(0, Math.max(soLuong, (vietTay || []).length));
}

/** Nhóm theo cụm, chỉ lấy trang đã viết — dùng cho trang danh mục. */
export function theoCum() {
  const ra = {};
  for (const x of DA_XUAT_BAN) {
    (ra[x.cum] = ra[x.cum] || []).push(x);
  }
  for (const k of Object.keys(ra)) {
    ra[k].sort((a, b) => a.term.localeCompare(b.term, 'vi'));
  }
  return ra;
}

/** Thứ tự viết: ưu tiên 1 trước, trong cùng mức thì theo cụm. */
export function thuTuViet() {
  return [...TU_DIEN].sort(
    (a, b) => a.priority - b.priority || a.cum.localeCompare(b.cum) || a.term.localeCompare(b.term, 'vi')
  );
}

export function thongKe() {
  const theoCum = {};
  const theoUuTien = { 1: 0, 2: 0, 3: 0 };
  for (const x of TU_DIEN) {
    theoCum[x.cum] = (theoCum[x.cum] || 0) + 1;
    theoUuTien[x.priority] += 1;
  }
  return {
    tong: TU_DIEN.length,
    daViet: DA_XUAT_BAN.length,
    theoCum,
    theoUuTien,
    khongDanToiCongCu: TU_DIEN.filter((x) => x.relatedTools.length === 0).map((x) => x.slug),
    /**
     * Nội dung đã viết nhưng không có mục nào trong khung nhận — thường do gõ
     * sai slug. Trang đó sẽ không bao giờ hiện ra, và không ai phát hiện cho
     * tới khi tự hỏi sao bài mình viết không thấy đâu.
     */
    noiDungMoCoi: Object.keys(NOI_DUNG_KEYS).filter(
      (k) => !TU_DIEN.some((x) => x.slug === k)
    ),
  };
}
